import { faEye, faPencil, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Space, Table, Tag, Tooltip } from "antd";
import { ActionStatus } from "common/enums";
import { FullHeightContainerLayout, ProhibitedArea } from "components";
import moment from "moment";
import React from "react";

const { Column } = Table;

interface DataSyndicationSubscriptionListProps {
    items: IDataSyndicationSubscription[];
    onViewSubscription: (subscriptionId: string) => void;
    onEditSubscription: (subscriptionId: string) => void;
    onDeleteSubscription?: (subscriptionId: string) => void;
    permission?: IPermissionState;
    confirmDataSyndicationSubscriptionActions?: Record<string, { status: string; error?: IActionError }>;
}

const DataSyndicationSubscriptionList: React.FC<DataSyndicationSubscriptionListProps> = ({
    items,
    onViewSubscription,
    onEditSubscription,
    onDeleteSubscription,
    permission = { canEdit: true, canView: true, canDelete: true, canAdd: true },
    confirmDataSyndicationSubscriptionActions = {},
}) => {
    if (!permission) {
        return <ProhibitedArea />;
    }

    return (
        <FullHeightContainerLayout
            showHeader={false}
            showFooter={true}
            content={
                <Table size="small" rowKey="subscriptionId" dataSource={items} pagination={false} scroll={{ y: "100vh" }} className="container-height-100">
                    <Column
                        width="6em"
                        render={(_, subscription: IDataSyndicationSubscription) => (
                            <Space wrap>
                                <Tooltip title="View subscription">
                                    <FontAwesomeIcon
                                        icon={faEye}
                                        className={permission.canView ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                        onClick={() => (permission.canView ? onViewSubscription(subscription.subscriptionId) : null)}
                                    />
                                </Tooltip>
                                {subscription.status === "PENDING" && (
                                    <Tooltip title="Confirm subscription">
                                        <FontAwesomeIcon
                                            icon={faPencil}
                                            className={permission.canEdit ? "action-cursor text-blue-500" : "text-gray-400 opacity-45"}
                                            onClick={() => (permission.canEdit ? onEditSubscription(subscription.subscriptionId) : null)}
                                        />
                                    </Tooltip>
                                )}
                            </Space>
                        )}
                    />
                    <Column
                        width="15%"
                        dataIndex="name"
                        title="Name"
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value: string) => (
                            <Tooltip placement="topLeft" title={value}>
                                {value}
                            </Tooltip>
                        )}
                    />
                    <Column
                        width="15%"
                        dataIndex="description"
                        title="Purpose"
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value: string) => (
                            <Tooltip placement="topLeft" title={value}>
                                {value}
                            </Tooltip>
                        )}
                    />
                    <Column
                        width="10%"
                        dataIndex="status"
                        title="Status"
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(status: string, subscription: IDataSyndicationSubscription) => {
                            const confirmAction = confirmDataSyndicationSubscriptionActions[subscription.subscriptionId];
                            if (confirmAction?.status === ActionStatus.SUCCESS) {
                                return <Tag color="green">CONFIRMED</Tag>;
                            }
                            return <Tag color={status === "PENDING" ? "orange" : "green"}>{status}</Tag>;
                        }}
                    />
                    <Column
                        dataIndex="validFrom"
                        title="Valid From"
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value: string) =>
                            value && (
                                <Tooltip placement="topLeft" title={moment.utc(value).toDate().toLocaleString()}>
                                    {moment.utc(value).toDate().toLocaleString()}
                                </Tooltip>
                            )
                        }
                    />
                    <Column
                        dataIndex="validUntil"
                        title="Valid Until"
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value: string) =>
                            value && (
                                <Tooltip placement="topLeft" title={moment.utc(value).toDate().toLocaleString()}>
                                    {moment.utc(value).toDate().toLocaleString()}
                                </Tooltip>
                            )
                        }
                    />
                    <Column
                        dataIndex="invocationDate"
                        title="Last Invoked Date"
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value: string) =>
                            value && (
                                <Tooltip placement="topLeft" title={moment.utc(value).toDate().toLocaleString()}>
                                    {moment.utc(value).toDate().toLocaleString()}
                                </Tooltip>
                            )
                        }
                    />
                    <Column
                        dataIndex="createdDate"
                        title="Created Date"
                        ellipsis={{
                            showTitle: false,
                        }}
                        render={(value: string) => (
                            <Tooltip placement="topLeft" title={moment.utc(value).toDate().toLocaleString()}>
                                {moment.utc(value).toDate().toLocaleString()}
                            </Tooltip>
                        )}
                    />
                    {onDeleteSubscription && (
                        <Column
                            width="3em"
                            align="center"
                            render={(_, subscription: IDataSyndicationSubscription) => (
                                <Tooltip title="Delete subscription">
                                    <FontAwesomeIcon
                                        icon={faTrashCan}
                                        className={permission.canDelete ? "action-cursor text-red-500" : "text-gray-400 opacity-45"}
                                        onClick={() => (permission.canDelete ? onDeleteSubscription(subscription.subscriptionId) : null)}
                                    />
                                </Tooltip>
                            )}
                        />
                    )}
                </Table>
            }
        />
    );
};

export default DataSyndicationSubscriptionList;
